import { Link } from 'react-router-dom';
import Navbar from './home/components/Navbar';
import Footer from './home/components/Footer';
import { useLanguage } from '../contexts/LanguageContext';
import { usePageSeo } from '../hooks/usePageSeo';
import { LinkedText } from '../components/LinkedText';

type Localized = { en: string; bm: string };
type TermsSection = {
  id: string;
  title: Localized;
  subsections: Array<{
    heading: Localized;
    body: Localized;
    listLabel?: Localized;
    bullets?: Localized[];
    link?: { to: string; label: Localized };
  }>;
};

const sections: TermsSection[] = [
  {
    id: 'acceptance',
    title: { en: '1. Acceptance of Terms', bm: '1. Penerimaan Terma' },
    subsections: [
      {
        heading: { en: 'Your agreement with the platform', bm: 'Persetujuan anda dengan platform' },
        body: {
          en: 'By accessing, browsing or using MrCuci, you acknowledge that you have read and agree to the terms that apply to your use. If you do not understand or agree with them, do not use the platform or its services.',
          bm: 'Dengan mengakses, melayari atau menggunakan MrCuci, anda mengakui bahawa anda telah membaca dan bersetuju dengan terma yang terpakai kepada penggunaan anda. Jika anda tidak memahami atau tidak bersetuju dengannya, jangan gunakan platform atau perkhidmatannya.',
        },
      },
      {
        heading: { en: 'Updates and your duty to review', bm: 'Kemas kini dan tanggungjawab anda untuk menyemak' },
        body: {
          en: 'These terms may be updated as services, legal requirements or operational practices change. You are responsible for reviewing the current version periodically and before making important account or payment decisions.',
          bm: 'Terma ini mungkin dikemas kini apabila perkhidmatan, keperluan undang-undang atau amalan operasi berubah. Anda bertanggungjawab menyemak versi semasa dari semasa ke semasa dan sebelum membuat keputusan penting berkaitan akaun atau pembayaran.',
        },
      },
    ],
  },
  {
    id: 'eligibility',
    title: { en: '2. Eligibility and Age Requirements', bm: '2. Kelayakan dan Keperluan Umur' },
    subsections: [
      {
        heading: { en: 'Adults only', bm: 'Untuk orang dewasa sahaja' },
        body: {
          en: 'You must be at least 18 years old, or the higher minimum legal age required in your jurisdiction, to use real-money services. Minors must not register, deposit, play or attempt to access an account.',
          bm: 'Anda mesti berumur sekurang-kurangnya 18 tahun, atau mencapai umur minimum undang-undang yang lebih tinggi di bidang kuasa anda, untuk menggunakan perkhidmatan wang sebenar. Individu bawah umur tidak boleh mendaftar, membuat deposit, bermain atau cuba mengakses akaun.',
        },
      },
      {
        heading: { en: 'Local-law responsibility', bm: 'Tanggungjawab terhadap undang-undang tempatan' },
        body: {
          en: 'You are responsible for confirming that access and participation are lawful where you are located. Availability of a website or feature does not itself mean that its use is permitted in every place.',
          bm: 'Anda bertanggungjawab memastikan bahawa akses dan penyertaan adalah sah di lokasi anda. Ketersediaan laman web atau ciri tidak dengan sendirinya bermakna penggunaannya dibenarkan di setiap tempat.',
        },
      },
    ],
  },
  {
    id: 'registration',
    title: { en: '3. Account Registration', bm: '3. Pendaftaran Akaun' },
    subsections: [
      {
        heading: { en: 'Accurate and complete information', bm: 'Maklumat yang tepat dan lengkap' },
        body: {
          en: 'Registration details must be truthful, complete and kept current. Your name, date of birth, contact details and payment information should belong to you and match any documents requested for verification.',
          bm: 'Butiran pendaftaran mestilah benar, lengkap dan sentiasa dikemas kini. Nama, tarikh lahir, butiran hubungan serta maklumat pembayaran hendaklah milik anda dan sepadan dengan dokumen yang mungkin diminta untuk pengesahan.',
        },
      },
      {
        heading: { en: 'One account and secure access', bm: 'Satu akaun dan akses yang selamat' },
        body: {
          en: 'Unless expressly permitted, each player may maintain only one account. Keep your password confidential, use a unique credential, sign out on shared devices and notify customer support if you suspect unauthorised access.',
          bm: 'Melainkan dibenarkan dengan nyata, setiap pemain hanya boleh mengekalkan satu akaun. Rahsiakan kata laluan, gunakan kelayakan yang unik, log keluar daripada peranti kongsi dan maklumkan sokongan pelanggan jika anda mengesyaki akses tanpa kebenaran.',
        },
      },
    ],
  },
  {
    id: 'verification',
    title: { en: '4. Account Verification', bm: '4. Pengesahan Akaun' },
    subsections: [
      {
        heading: { en: 'What may be verified', bm: 'Perkara yang mungkin disahkan' },
        body: {
          en: 'Subject to applicable requirements, MrCuci may request information or documents to verify identity, age, account ownership, address or ownership of a payment method. Checks may also be used to protect accounts and review unusual activity.',
          bm: 'Tertakluk pada keperluan yang terpakai, MrCuci mungkin meminta maklumat atau dokumen untuk mengesahkan identiti, umur, pemilikan akaun, alamat atau pemilikan kaedah pembayaran. Semakan juga boleh digunakan untuk melindungi akaun dan menilai aktiviti luar biasa.',
        },
      },
      {
        heading: { en: 'Verification before withdrawal', bm: 'Pengesahan sebelum pengeluaran' },
        body: {
          en: 'Verification may be required before a withdrawal is approved or while a payment review is in progress. Provide readable, valid information through official channels; incomplete or inconsistent details may delay the review.',
          bm: 'Pengesahan mungkin diperlukan sebelum pengeluaran diluluskan atau semasa semakan pembayaran dijalankan. Berikan maklumat yang jelas dibaca dan sah melalui saluran rasmi; butiran yang tidak lengkap atau tidak konsisten mungkin melambatkan semakan.',
        },
      },
    ],
  },
  {
    id: 'deposits',
    title: { en: '5. Deposits and Account Funds', bm: '5. Deposit dan Dana Akaun' },
    subsections: [
      {
        heading: { en: 'Methods, currency and availability', bm: 'Kaedah, mata wang dan ketersediaan' },
        body: {
          en: 'Supported deposit methods may vary by account, location, provider and time. MYR may be available where supported. Check the cashier information shown to you before confirming, including the currency, amount, account name, fees if any, and any applicable limits.',
          bm: 'Kaedah deposit yang disokong mungkin berbeza mengikut akaun, lokasi, penyedia dan masa. MYR mungkin tersedia jika disokong. Semak maklumat juruwang yang dipaparkan sebelum mengesahkan, termasuk mata wang, jumlah, nama akaun, fi jika ada dan apa-apa had yang terpakai.',
        },
        listLabel: { en: 'Pre-deposit checklist', bm: 'Senarai semak sebelum deposit' },
        bullets: [
          { en: 'Confirm that the payment details and destination are correct.', bm: 'Pastikan butiran pembayaran dan destinasi adalah betul.' },
          { en: 'Use a payment method you are authorised to use.', bm: 'Gunakan kaedah pembayaran yang anda dibenarkan gunakan.' },
          { en: 'Keep the transaction reference until the balance is credited.', bm: 'Simpan rujukan transaksi sehingga baki dikreditkan.' },
        ],
      },
      {
        heading: { en: 'Delays and missing credits', bm: 'Kelewatan dan kredit yang belum diterima' },
        body: {
          en: 'Bank, network, provider or review delays may affect when funds appear. Do not repeatedly send the same payment if its status is unclear; retain the receipt and contact customer support with the relevant reference and time.',
          bm: 'Kelewatan bank, rangkaian, penyedia atau semakan mungkin menjejaskan masa dana dipaparkan. Jangan menghantar pembayaran yang sama berulang kali jika statusnya tidak jelas; simpan resit dan hubungi sokongan pelanggan dengan rujukan serta masa berkaitan.',
        },
      },
    ],
  },
  {
    id: 'withdrawals',
    title: { en: '6. Withdrawals', bm: '6. Pengeluaran' },
    subsections: [
      {
        heading: { en: 'Submitting a withdrawal request', bm: 'Menghantar permintaan pengeluaran' },
        body: {
          en: 'Enter the requested amount and payment details carefully. A withdrawal may be subject to account verification, payment ownership checks, available-balance rules, bonus conditions, provider availability and other applicable requirements.',
          bm: 'Masukkan jumlah dan butiran pembayaran yang diminta dengan teliti. Pengeluaran mungkin tertakluk pada pengesahan akaun, semakan pemilikan pembayaran, peraturan baki tersedia, syarat bonus, ketersediaan penyedia dan keperluan lain yang terpakai.',
        },
      },
      {
        heading: { en: 'Processing time and pending requests', bm: 'Masa pemprosesan dan permintaan tertangguh' },
        body: {
          en: 'Processing times can vary according to verification status, transaction review, payment provider, banking schedules and the accuracy of submitted details. While a request is pending, avoid changing relevant account information and use its reference when asking support for an update.',
          bm: 'Masa pemprosesan boleh berbeza mengikut status pengesahan, semakan transaksi, penyedia pembayaran, jadual perbankan dan ketepatan butiran yang dihantar. Semasa permintaan masih tertangguh, elakkan mengubah maklumat akaun berkaitan dan gunakan rujukannya apabila meminta kemas kini daripada sokongan.',
        },
      },
    ],
  },
  {
    id: 'bonuses',
    title: { en: '7. Bonuses and Promotions', bm: '7. Bonus dan Promosi' },
    subsections: [
      {
        heading: { en: 'Each offer has separate rules', bm: 'Setiap tawaran mempunyai peraturan berasingan' },
        body: {
          en: 'A bonus or promotion may have its own eligibility, qualifying-deposit, game contribution, wagering or turnover, expiry, withdrawal and claim rules. Read the specific offer terms before opting in because those terms apply in addition to this page.',
          bm: 'Bonus atau promosi mungkin mempunyai syarat tersendiri berkaitan kelayakan, deposit layak, sumbangan permainan, pertaruhan atau pusing ganti, tempoh luput, pengeluaran dan tuntutan. Baca terma tawaran khusus sebelum menyertainya kerana terma tersebut terpakai sebagai tambahan kepada halaman ini.',
        },
        link: { to: '/bonus/', label: { en: 'Review current bonus information', bm: 'Semak maklumat bonus semasa' } },
      },
      {
        heading: { en: 'Expiry, restrictions and abuse', bm: 'Tempoh luput, sekatan dan penyalahgunaan' },
        body: {
          en: 'Bonus balances or winnings may be affected if requirements are not met before expiry. Multiple-account claims, coordinated play, manipulation, false eligibility details or attempts to bypass an offer rule may lead to review, cancellation or account restrictions.',
          bm: 'Baki bonus atau kemenangan mungkin terjejas jika keperluan tidak dipenuhi sebelum tamat tempoh. Tuntutan melalui berbilang akaun, permainan terancang, manipulasi, butiran kelayakan palsu atau cubaan memintas peraturan tawaran boleh membawa kepada semakan, pembatalan atau sekatan akaun.',
        },
      },
    ],
  },
  {
    id: 'games',
    title: { en: '8. Casino Games and Gameplay', bm: '8. Permainan Kasino dan Cara Bermain' },
    subsections: [
      {
        heading: { en: 'Game categories and rules', bm: 'Kategori dan peraturan permainan' },
        body: {
          en: 'Available categories may include slots, live casino, table games, jackpot games and fishing games. Availability can vary. Read the paytable, controls, bet limits, feature rules and provider instructions for each game before placing a wager.',
          bm: 'Kategori yang tersedia mungkin termasuk slot, kasino langsung, permainan meja, permainan jackpot dan permainan memancing. Ketersediaan boleh berbeza. Baca jadual bayaran, kawalan, had pertaruhan, peraturan ciri dan arahan penyedia bagi setiap permainan sebelum membuat pertaruhan.',
        },
        link: { to: '/games/', label: { en: 'Explore game information', bm: 'Terokai maklumat permainan' } },
      },
      {
        heading: { en: 'Chance, risk and interruptions', bm: 'Peluang, risiko dan gangguan' },
        body: {
          en: 'Casino outcomes involve chance and financial risk; previous results do not guarantee future outcomes. If a game is interrupted, the provider and platform records may determine whether a wager was accepted and how the completed or unresolved round is handled.',
          bm: 'Keputusan kasino melibatkan nasib dan risiko kewangan; keputusan lalu tidak menjamin keputusan akan datang. Jika permainan terganggu, rekod penyedia dan platform mungkin menentukan sama ada pertaruhan telah diterima serta cara pusingan yang selesai atau belum selesai dikendalikan.',
        },
      },
    ],
  },
  {
    id: 'technical-errors',
    title: { en: '9. Game Errors and Technical Issues', bm: '9. Ralat Permainan dan Isu Teknikal' },
    subsections: [
      {
        heading: { en: 'Display errors and system records', bm: 'Ralat paparan dan rekod sistem' },
        body: {
          en: 'An incorrect balance, paytable, result, credit or other display caused by a technical error does not necessarily represent a valid transaction. Relevant game, provider and transaction records may be reviewed to establish the recorded outcome and correct an obvious error where appropriate.',
          bm: 'Baki, jadual bayaran, keputusan, kredit atau paparan lain yang tidak betul akibat ralat teknikal tidak semestinya mewakili transaksi yang sah. Rekod permainan, penyedia dan transaksi berkaitan mungkin disemak untuk menentukan keputusan yang direkodkan dan membetulkan ralat jelas jika sesuai.',
        },
      },
      {
        heading: { en: 'Service continuity', bm: 'Kesinambungan perkhidmatan' },
        body: {
          en: 'Continuous or error-free availability is not guaranteed. Maintenance, internet conditions, device compatibility, provider downtime or events outside reasonable control may interrupt access; save useful details and report a recurring issue.',
          bm: 'Ketersediaan berterusan atau bebas ralat tidak dijamin. Penyelenggaraan, keadaan internet, keserasian peranti, gangguan penyedia atau kejadian di luar kawalan munasabah mungkin mengganggu akses; simpan butiran berguna dan laporkan isu yang berulang.',
        },
      },
    ],
  },
  {
    id: 'prohibited-activities',
    title: { en: '10. Fair Use and Prohibited Activities', bm: '10. Penggunaan Adil dan Aktiviti Dilarang' },
    subsections: [
      {
        heading: { en: 'Honest account and payment use', bm: 'Penggunaan akaun dan pembayaran secara jujur' },
        body: {
          en: 'You must use the service honestly and only through an account and payment method you are authorised to control.',
          bm: 'Anda mesti menggunakan perkhidmatan secara jujur dan hanya melalui akaun serta kaedah pembayaran yang anda diberi kuasa untuk kawal.',
        },
        listLabel: { en: 'Prohibited conduct includes', bm: 'Tingkah laku yang dilarang termasuk' },
        bullets: [
          { en: 'Providing false data, using another person’s account or maintaining unauthorised multiple accounts.', bm: 'Memberikan data palsu, menggunakan akaun orang lain atau mengekalkan berbilang akaun tanpa kebenaran.' },
          { en: 'Manipulating promotions, exploiting errors or coordinating activity to gain an unfair advantage.', bm: 'Memanipulasi promosi, mengeksploitasi ralat atau menyelaraskan aktiviti untuk memperoleh kelebihan tidak adil.' },
          { en: 'Payment fraud, chargeback abuse or use of funds without authorisation.', bm: 'Penipuan pembayaran, penyalahgunaan caj balik atau penggunaan dana tanpa kebenaran.' },
        ],
      },
      {
        heading: { en: 'Systems, access and automation', bm: 'Sistem, akses dan automasi' },
        body: {
          en: 'Do not interfere with security, probe or bypass access controls, introduce harmful code, obtain unauthorised access, scrape protected systems or use bots, scripts or other prohibited automation. Suspected activity may be investigated and access may be restricted.',
          bm: 'Jangan mengganggu keselamatan, menguji atau memintas kawalan akses, memperkenalkan kod berbahaya, memperoleh akses tanpa kebenaran, mengaut sistem dilindungi atau menggunakan bot, skrip atau automasi lain yang dilarang. Aktiviti yang disyaki mungkin disiasat dan akses mungkin dihadkan.',
        },
      },
    ],
  },
  {
    id: 'responsible-gaming',
    title: { en: '11. Responsible Gaming', bm: '11. Permainan Bertanggungjawab' },
    subsections: [
      {
        heading: { en: 'Entertainment, not income', bm: 'Hiburan, bukan sumber pendapatan' },
        body: {
          en: 'Treat gambling as paid entertainment, never as a way to earn income or solve financial problems. Only risk money you can afford to lose, never use money needed for food, housing, bills or debt, and do not chase losses.',
          bm: 'Anggap perjudian sebagai hiburan berbayar, bukan cara untuk memperoleh pendapatan atau menyelesaikan masalah kewangan. Hanya pertaruhkan wang yang anda mampu rugi, jangan gunakan wang untuk makanan, tempat tinggal, bil atau hutang, dan jangan mengejar kerugian.',
        },
      },
      {
        heading: { en: 'Limits, breaks and support', bm: 'Had, rehat dan sokongan' },
        body: {
          en: 'Set personal time and spending limits before playing, monitor your activity and take regular breaks. Stop and seek help if play affects your finances, work, relationships or wellbeing.',
          bm: 'Tetapkan had masa dan perbelanjaan peribadi sebelum bermain, pantau aktiviti anda dan berehat secara berkala. Berhenti dan dapatkan bantuan jika permainan menjejaskan kewangan, pekerjaan, hubungan atau kesejahteraan anda.',
        },
        link: { to: '/responsible-gaming/', label: { en: 'Read the Responsible Gaming guide', bm: 'Baca panduan Permainan Bertanggungjawab' } },
      },
    ],
  },
  {
    id: 'minors',
    title: { en: '12. Protection of Minors', bm: '12. Perlindungan Individu Bawah Umur' },
    subsections: [
      {
        heading: { en: 'Practical controls on shared devices', bm: 'Kawalan praktikal pada peranti kongsi' },
        body: {
          en: 'Adults who share a household or device with minors should log out after each session, avoid saving passwords or payment details, secure devices with a passcode and keep account communications out of reach.',
          bm: 'Orang dewasa yang berkongsi rumah atau peranti dengan individu bawah umur hendaklah log keluar selepas setiap sesi, elakkan menyimpan kata laluan atau butiran pembayaran, lindungi peranti dengan kod laluan dan simpan komunikasi akaun di tempat yang tidak boleh dicapai.',
        },
      },
      {
        heading: { en: 'Preventing accidental access', bm: 'Mencegah akses tidak sengaja' },
        body: {
          en: 'Use device-level parental controls or filtering tools where appropriate. Do not permit a minor to watch, operate or fund real-money play, even under adult supervision.',
          bm: 'Gunakan kawalan ibu bapa pada peranti atau alat penapisan jika sesuai. Jangan benarkan individu bawah umur melihat, mengendalikan atau membiayai permainan wang sebenar, walaupun di bawah pengawasan orang dewasa.',
        },
      },
    ],
  },
  {
    id: 'privacy',
    title: { en: '13. Privacy and Personal Information', bm: '13. Privasi dan Maklumat Peribadi' },
    subsections: [
      {
        heading: { en: 'Operational use of information', bm: 'Penggunaan maklumat untuk operasi' },
        body: {
          en: 'Account, device, transaction, communication and verification information may be processed for platform operation, customer support, payment handling, security, fraud prevention and compliance with applicable requirements. Keep your profile and contact details accurate and current.',
          bm: 'Maklumat akaun, peranti, transaksi, komunikasi dan pengesahan mungkin diproses untuk operasi platform, sokongan pelanggan, pengendalian pembayaran, keselamatan, pencegahan penipuan dan pematuhan keperluan yang terpakai. Pastikan profil dan butiran hubungan anda tepat serta terkini.',
        },
      },
      {
        heading: { en: 'Phishing and official channels', bm: 'Pancingan data dan saluran rasmi' },
        body: {
          en: 'Be alert to phishing messages and lookalike websites. Use official channels, verify links before opening them, and never send your full password to customer support or disclose one-time security codes to an unverified person.',
          bm: 'Berwaspada terhadap mesej pancingan data dan laman web tiruan. Gunakan saluran rasmi, sahkan pautan sebelum membukanya, dan jangan sekali-kali menghantar kata laluan penuh kepada sokongan pelanggan atau mendedahkan kod keselamatan sekali guna kepada individu yang tidak disahkan.',
        },
      },
    ],
  },
  {
    id: 'intellectual-property',
    title: { en: '14. Intellectual Property', bm: '14. Harta Intelek' },
    subsections: [
      {
        heading: { en: 'Protected platform materials', bm: 'Bahan platform yang dilindungi' },
        body: {
          en: 'Branding, logos, written content, graphics, software, game presentation and interface elements may be owned by or licensed to their respective rights holders and are protected by applicable intellectual-property rules.',
          bm: 'Penjenamaan, logo, kandungan bertulis, grafik, perisian, persembahan permainan dan elemen antara muka mungkin dimiliki oleh atau dilesenkan kepada pemegang hak masing-masing serta dilindungi oleh peraturan harta intelek yang terpakai.',
        },
      },
      {
        heading: { en: 'Permitted personal use', bm: 'Penggunaan peribadi yang dibenarkan' },
        body: {
          en: 'You may use the interface for its intended personal purpose. Do not copy, republish, sell, modify, reverse engineer, remove notices from or commercially exploit protected materials unless you have prior permission or a legal right to do so.',
          bm: 'Anda boleh menggunakan antara muka untuk tujuan peribadi yang dimaksudkan. Jangan menyalin, menerbitkan semula, menjual, mengubah suai, melakukan kejuruteraan songsang, membuang notis atau mengeksploitasi bahan dilindungi secara komersial melainkan anda mempunyai kebenaran awal atau hak undang-undang.',
        },
      },
    ],
  },
  {
    id: 'third-parties',
    title: { en: '15. Third-Party Game and Payment Providers', bm: '15. Penyedia Permainan dan Pembayaran Pihak Ketiga' },
    subsections: [
      {
        heading: { en: 'Provider-dependent services', bm: 'Perkhidmatan yang bergantung pada penyedia' },
        body: {
          en: 'Some games, payment channels and technical functions are supplied by third parties. Their availability, maintenance, processing windows and technical rules may affect your experience and may differ by device, account or location.',
          bm: 'Sesetengah permainan, saluran pembayaran dan fungsi teknikal disediakan oleh pihak ketiga. Ketersediaan, penyelenggaraan, tempoh pemprosesan dan peraturan teknikal mereka mungkin menjejaskan pengalaman anda serta berbeza mengikut peranti, akaun atau lokasi.',
        },
      },
      {
        heading: { en: 'Changes to available options', bm: 'Perubahan pada pilihan yang tersedia' },
        body: {
          en: 'Where appropriate, providers, games or payment options may be added, removed, suspended or updated. Check the current platform display rather than relying on an older screenshot, message or previous transaction.',
          bm: 'Jika sesuai, penyedia, permainan atau pilihan pembayaran mungkin ditambah, dibuang, digantung atau dikemas kini. Semak paparan platform semasa dan jangan bergantung pada tangkapan skrin, mesej atau transaksi lama.',
        },
      },
    ],
  },
  {
    id: 'suspension-closure',
    title: { en: '16. Account Suspension or Closure', bm: '16. Penggantungan atau Penutupan Akaun' },
    subsections: [
      {
        heading: { en: 'Platform reviews and restrictions', bm: 'Semakan dan sekatan platform' },
        body: {
          en: 'An account may be restricted, suspended or closed where reasonably necessary for security, incomplete verification, suspected prohibited activity, payment review, breach of these terms or applicable requirements. Access to transactions may remain limited while a review is completed.',
          bm: 'Akaun mungkin dihadkan, digantung atau ditutup apabila munasabah diperlukan untuk keselamatan, pengesahan tidak lengkap, aktiviti dilarang yang disyaki, semakan pembayaran, pelanggaran terma ini atau keperluan yang terpakai. Akses kepada transaksi mungkin kekal terhad semasa semakan diselesaikan.',
        },
      },
      {
        heading: { en: 'Voluntary closure', bm: 'Penutupan secara sukarela' },
        body: {
          en: 'You may ask customer support about closing your account. If the request relates to loss of control or gambling harm, say so clearly and review the responsible-gaming support information rather than treating closure as a temporary break.',
          bm: 'Anda boleh bertanya kepada sokongan pelanggan tentang penutupan akaun. Jika permintaan berkaitan kehilangan kawalan atau mudarat perjudian, nyatakan dengan jelas dan semak maklumat sokongan permainan bertanggungjawab dan bukan menganggap penutupan sebagai rehat sementara.',
        },
        link: { to: '/responsible-gaming/', label: { en: 'View responsible-gaming support', bm: 'Lihat sokongan permainan bertanggungjawab' } },
      },
    ],
  },
  {
    id: 'support-disputes',
    title: { en: '17. Customer Support and Disputes', bm: '17. Sokongan Pelanggan dan Pertikaian' },
    subsections: [
      {
        heading: { en: 'Reporting an issue clearly', bm: 'Melaporkan isu dengan jelas' },
        body: {
          en: 'For an account, payment, bonus or game issue, provide your member ID, relevant date and time, amount, game or provider name, transaction reference, a clear description and screenshots where available. Never send your full password.',
          bm: 'Untuk isu akaun, pembayaran, bonus atau permainan, berikan ID ahli, tarikh dan masa berkaitan, jumlah, nama permainan atau penyedia, rujukan transaksi, penerangan yang jelas serta tangkapan skrin jika tersedia. Jangan sekali-kali menghantar kata laluan penuh anda.',
        },
      },
      {
        heading: { en: 'Records used for review', bm: 'Rekod yang digunakan untuk semakan' },
        body: {
          en: 'Account, transaction, payment and game records may be examined when reviewing a dispute. Keep your own receipts and support references, respond to reasonable questions, and allow the relevant records to be checked before submitting duplicate reports.',
          bm: 'Rekod akaun, transaksi, pembayaran dan permainan mungkin diperiksa semasa menyemak pertikaian. Simpan resit serta rujukan sokongan anda, jawab pertanyaan yang munasabah dan benarkan rekod berkaitan diperiksa sebelum menghantar laporan pendua.',
        },
      },
    ],
  },
  {
    id: 'service-changes',
    title: { en: '18. Changes to Services', bm: '18. Perubahan kepada Perkhidmatan' },
    subsections: [
      {
        heading: { en: 'Platform features may evolve', bm: 'Ciri platform mungkin berubah' },
        body: {
          en: 'Features, interfaces, games, promotions, payment channels and other services may be introduced, updated, paused or removed. A change may result from maintenance, security, provider availability, user experience or applicable requirements.',
          bm: 'Ciri, antara muka, permainan, promosi, saluran pembayaran dan perkhidmatan lain mungkin diperkenalkan, dikemas kini, dihentikan sementara atau dibuang. Perubahan mungkin berpunca daripada penyelenggaraan, keselamatan, ketersediaan penyedia, pengalaman pengguna atau keperluan yang terpakai.',
        },
      },
      {
        heading: { en: 'Check information at the point of use', bm: 'Semak maklumat ketika menggunakan perkhidmatan' },
        body: {
          en: 'Review the information displayed when you use a feature or confirm a transaction. If an option is unavailable or materially different from what you expected, pause and ask customer support before proceeding.',
          bm: 'Semak maklumat yang dipaparkan ketika anda menggunakan ciri atau mengesahkan transaksi. Jika sesuatu pilihan tidak tersedia atau berbeza dengan ketara daripada jangkaan anda, berhenti seketika dan tanya sokongan pelanggan sebelum meneruskan.',
        },
      },
    ],
  },
  {
    id: 'terms-changes',
    title: { en: '19. Changes to Terms', bm: '19. Perubahan kepada Terma' },
    subsections: [
      {
        heading: { en: 'Periodic review', bm: 'Semakan berkala' },
        body: {
          en: 'These Terms & Conditions may be revised periodically to reflect service, security, provider, operational or applicable legal changes. The last-reviewed date helps identify the version presented on this page.',
          bm: 'Terma & Syarat ini mungkin disemak dari semasa ke semasa bagi mencerminkan perubahan perkhidmatan, keselamatan, penyedia, operasi atau undang-undang yang terpakai. Tarikh semakan terakhir membantu mengenal pasti versi yang dipaparkan pada halaman ini.',
        },
      },
      {
        heading: { en: 'Communication and disagreement', bm: 'Komunikasi dan ketidaksetujuan' },
        body: {
          en: 'Important changes may be communicated through the website, account interface or another appropriate channel. Review any notice and the revised text. If you disagree with the updated terms, stop using the services and contact support about any necessary account steps.',
          bm: 'Perubahan penting mungkin dimaklumkan melalui laman web, antara muka akaun atau saluran lain yang sesuai. Semak sebarang notis dan teks yang disemak. Jika anda tidak bersetuju dengan terma yang dikemas kini, berhenti menggunakan perkhidmatan dan hubungi sokongan tentang langkah akaun yang diperlukan.',
        },
      },
    ],
  },
];

const faqs: Array<{ question: Localized; answer: Localized }> = [
  {
    question: { en: 'Why should I read the MrCuci Terms & Conditions?', bm: 'Mengapa saya perlu membaca Terma & Syarat MrCuci?' },
    answer: {
      en: 'They explain the rules and responsibilities that apply to accounts, payments, withdrawals, bonuses, games, security and platform use. Reading them before using a feature helps you understand what information may be required and what conduct is not allowed.',
      bm: 'Terma ini menerangkan peraturan dan tanggungjawab yang terpakai kepada akaun, pembayaran, pengeluaran, bonus, permainan, keselamatan dan penggunaan platform. Membacanya sebelum menggunakan sesuatu ciri membantu anda memahami maklumat yang mungkin diperlukan dan tingkah laku yang tidak dibenarkan.',
    },
  },
  {
    question: { en: 'Do bonuses have separate terms?', bm: 'Adakah bonus mempunyai terma berasingan?' },
    answer: {
      en: 'Yes. Each promotion may have separate eligibility, deposit, game contribution, turnover, expiry, claim and withdrawal rules. Those offer-specific rules apply together with these general terms, so review the promotion details before opting in.',
      bm: 'Ya. Setiap promosi mungkin mempunyai syarat berasingan tentang kelayakan, deposit, sumbangan permainan, pusing ganti, tempoh luput, tuntutan dan pengeluaran. Peraturan khusus tawaran itu terpakai bersama terma umum ini, jadi semak butiran promosi sebelum menyertainya.',
    },
  },
  {
    question: { en: 'Can bonus requirements affect a withdrawal?', bm: 'Bolehkah keperluan bonus menjejaskan pengeluaran?' },
    answer: {
      en: 'They may. If a bonus is active, unmet wagering or turnover requirements, excluded games, expiry rules or a maximum-withdrawal condition may affect the bonus balance or withdrawal request. Check the applicable offer terms and account status first.',
      bm: 'Ya, mungkin. Jika bonus masih aktif, keperluan pertaruhan atau pusing ganti yang belum dipenuhi, permainan terkecuali, peraturan tamat tempoh atau syarat pengeluaran maksimum mungkin menjejaskan baki bonus atau permintaan pengeluaran. Semak terma tawaran dan status akaun terlebih dahulu.',
    },
  },
  {
    question: { en: 'Why may account verification be required?', bm: 'Mengapa pengesahan akaun mungkin diperlukan?' },
    answer: {
      en: 'Verification may help confirm age, identity, account ownership and payment details, protect against unauthorised use and satisfy applicable requirements. It may be requested during account review or before a withdrawal can proceed.',
      bm: 'Pengesahan boleh membantu memastikan umur, identiti, pemilikan akaun dan butiran pembayaran, melindungi daripada penggunaan tanpa kebenaran serta memenuhi keperluan yang terpakai. Ia mungkin diminta semasa semakan akaun atau sebelum pengeluaran boleh diteruskan.',
    },
  },
  {
    question: { en: 'Can these terms change?', bm: 'Bolehkah terma ini berubah?' },
    answer: {
      en: 'Yes. The terms may be updated when services, providers, security practices, operations or applicable requirements change. Review the last-reviewed date and any notice shown through the website or account interface.',
      bm: 'Ya. Terma mungkin dikemas kini apabila perkhidmatan, penyedia, amalan keselamatan, operasi atau keperluan yang terpakai berubah. Semak tarikh semakan terakhir dan sebarang notis yang dipaparkan melalui laman web atau antara muka akaun.',
    },
  },
  {
    question: { en: 'What should I do if I disagree with the terms?', bm: 'Apakah yang perlu saya lakukan jika tidak bersetuju dengan terma?' },
    answer: {
      en: 'Do not use, or stop using, the platform services. Contact customer support if you need help understanding an account consequence, resolving a pending matter or requesting closure; disagreement does not permit continued use outside the applicable rules.',
      bm: 'Jangan gunakan, atau berhenti menggunakan, perkhidmatan platform. Hubungi sokongan pelanggan jika anda memerlukan bantuan memahami kesan terhadap akaun, menyelesaikan perkara tertangguh atau meminta penutupan; ketidaksetujuan tidak membenarkan penggunaan berterusan di luar peraturan yang terpakai.',
    },
  },
];

const tocItems = [
  ...sections.map(({ id, title }) => ({ id, title })),
  { id: 'terms-faq', title: { en: '20. Terms FAQ', bm: '20. Soalan Lazim Terma' } },
];

export default function TermsPage() {
  const { lang, t } = useLanguage();
  const pick = (value: Localized) => (lang === 'bm' ? value.bm : value.en);
  const language = lang === 'bm' ? 'ms-MY' : 'en-MY';
  const title = t(
    'MrCuci Terms and Conditions | Online Casino Malaysia Terms',
    'Terma dan Syarat MrCuci | Terma Kasino Dalam Talian Malaysia'
  );
  const description = t(
    'Read MrCuci Terms and Conditions for online casino Malaysia accounts, MYR payments where supported, withdrawals, bonus terms, games, security and responsible gaming.',
    'Baca Terma dan Syarat MrCuci untuk akaun kasino dalam talian Malaysia, pembayaran MYR jika disokong, pengeluaran, terma bonus, permainan, keselamatan dan permainan bertanggungjawab.'
  );
  const faqEntities = faqs.map(({ question, answer }) => ({
    '@type': 'Question',
    name: pick(question),
    acceptedAnswer: { '@type': 'Answer', text: pick(answer) },
  }));

  usePageSeo({
    title,
    description,
    path: '/terms/',
    language,
    keywords: [
      'MrCuci Terms and Conditions',
      'online casino Malaysia terms',
      'MrCuci account terms',
      'MYR payment terms',
      'online casino withdrawal terms Malaysia',
      'MrCuci bonus terms',
      'responsible gaming Malaysia',
      'Terma dan Syarat MrCuci',
      'terma kasino dalam talian Malaysia',
    ],
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'BreadcrumbList',
          '@id': 'https://www.mrcuci.me/terms/#breadcrumb',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: t('Home', 'Laman Utama'), item: 'https://www.mrcuci.me/' },
            { '@type': 'ListItem', position: 2, name: t('Terms & Conditions', 'Terma & Syarat'), item: 'https://www.mrcuci.me/terms/' },
          ],
        },
        {
          '@type': 'WebPage',
          '@id': 'https://www.mrcuci.me/terms/#webpage',
          url: 'https://www.mrcuci.me/terms/',
          name: title,
          description,
          inLanguage: language,
          isPartOf: { '@id': 'https://www.mrcuci.me/#website' },
          breadcrumb: { '@id': 'https://www.mrcuci.me/terms/#breadcrumb' },
          dateModified: '2026-08-13',
          audience: {
            '@type': 'PeopleAudience',
            suggestedMinAge: 18,
            geographicArea: { '@type': 'Country', name: 'Malaysia' },
          },
          about: [
            'Account registration and verification',
            'MYR payments where supported',
            'Withdrawals and bonus terms',
            'Casino game rules and security',
            'Responsible gaming',
          ],
        },
        {
          '@type': 'FAQPage',
          '@id': 'https://www.mrcuci.me/terms/#faq',
          url: 'https://www.mrcuci.me/terms/#terms-faq',
          inLanguage: language,
          isPartOf: { '@id': 'https://www.mrcuci.me/terms/#webpage' },
          mainEntity: faqEntities,
        },
      ],
    },
  });

  return (
    <div className="min-h-screen bg-[#080000] font-sans">
      <Navbar />
      <main className="pt-20">
        <section
          className="border-b border-[#d4a017]/10 py-14 md:py-20"
          style={{ background: 'linear-gradient(135deg, #0d0000 0%, #1a0303 55%, #0d0000 100%)' }}
        >
          <div className="mx-auto max-w-4xl px-4 md:px-6">
            <p className="mb-4 text-xs font-semibold uppercase tracking-widest text-[#d4a017]">
              {t('Legal Information', 'Maklumat Undang-Undang')}
            </p>
            <h1 className="mb-5 text-3xl font-black leading-tight text-white md:text-5xl">
              <LinkedText>{t(
                'MrCuci Terms & Conditions – Online Casino Malaysia',
                'Terma & Syarat MrCuci – Kasino Dalam Talian Malaysia'
              )}</LinkedText>
            </h1>
            <div className="max-w-3xl space-y-4 text-sm leading-[1.85] text-gray-300 md:text-base">
              <p>
                <LinkedText>{t(
                  'These Terms & Conditions explain the rules for account registration, deposits and withdrawals, bonuses and promotions, casino games, security, responsible gaming and other platform services.',
                  'Terma & Syarat ini menerangkan peraturan bagi pendaftaran akaun, deposit dan pengeluaran, bonus dan promosi, permainan kasino, keselamatan, permainan bertanggungjawab serta perkhidmatan platform lain.'
                )}</LinkedText>
              </p>
              <p>
                <LinkedText>{t(
                  'Use of the platform means you should read and understand the terms that apply to the service or transaction you choose. These page terms should be read together with applicable promotion, game, payment, privacy and responsible-gaming rules.',
                  'Penggunaan platform bermakna anda perlu membaca dan memahami terma yang terpakai kepada perkhidmatan atau transaksi pilihan anda. Terma halaman ini hendaklah dibaca bersama peraturan promosi, permainan, pembayaran, privasi dan permainan bertanggungjawab yang berkenaan.'
                )}</LinkedText>
              </p>
            </div>
            <p className="mt-5 text-xs text-gray-500">
              {t('Last reviewed: 13 August 2026', 'Semakan terakhir: 13 Ogos 2026')}
            </p>
          </div>
        </section>

        <nav
          aria-label={t('Terms contents', 'Kandungan terma')}
          className="border-b border-[#d4a017]/10"
          style={{ background: '#0d0101' }}
        >
          <div className="mx-auto max-w-4xl px-4 py-7 md:px-6">
            <h2 className="mb-4 text-lg font-black text-white">
              {t('Terms & Conditions contents', 'Kandungan Terma & Syarat')}
            </h2>
            <ol className="grid gap-2 text-sm sm:grid-cols-2">
              {tocItems.map(({ id, title: itemTitle }) => (
                <li key={id}>
                  <a
                    href={`#${id}`}
                    className="block rounded-lg border border-[#d4a017]/10 px-4 py-3 text-gray-400 transition-colors hover:border-[#d4a017]/40 hover:text-[#d4a017]"
                  >
                    {pick(itemTitle)}
                  </a>
                </li>
              ))}
            </ol>
          </div>
        </nav>

        <section className="py-12 md:py-16">
          <div className="mx-auto max-w-4xl space-y-8 px-4 md:px-6">
            {sections.map((section) => (
              <article
                id={section.id}
                key={section.id}
                className="scroll-mt-24 rounded-2xl border border-[#d4a017]/10 p-6 md:p-8"
                style={{ background: 'linear-gradient(135deg, #180303, #1e0404)' }}
              >
                <h2 className="mb-5 text-xl font-black text-white md:text-2xl">{pick(section.title)}</h2>
                <div className="space-y-6">
                  {section.subsections.map((subsection) => (
                    <section key={subsection.heading.en}>
                      <h3 className="mb-2 text-lg font-bold text-[#d4a017]">{pick(subsection.heading)}</h3>
                      <p className="text-sm leading-[1.85] text-gray-400"><LinkedText>{pick(subsection.body)}</LinkedText></p>
                      {subsection.bullets && subsection.listLabel && (
                        <>
                          <h4 className="mb-2 mt-4 text-base font-semibold text-white">{pick(subsection.listLabel)}</h4>
                          <ul className="list-disc space-y-2 pl-5 text-sm leading-[1.75] text-gray-400">
                            {subsection.bullets.map((bullet) => <li key={bullet.en}><LinkedText>{pick(bullet)}</LinkedText></li>)}
                          </ul>
                        </>
                      )}
                      {subsection.link && (
                        <Link
                          to={subsection.link.to}
                          className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-[#d4a017] hover:underline"
                        >
                          {pick(subsection.link.label)}
                          <i className="ri-arrow-right-line" aria-hidden="true" />
                        </Link>
                      )}
                    </section>
                  ))}
                </div>
              </article>
            ))}

            <article
              id="terms-faq"
              className="scroll-mt-24 rounded-2xl border border-[#d4a017]/10 p-6 md:p-8"
              style={{ background: 'linear-gradient(135deg, #180303, #1e0404)' }}
            >
              <h2 className="mb-5 text-xl font-black text-white md:text-2xl">
                {t('20. Terms FAQ', '20. Soalan Lazim Terma')}
              </h2>
              <div className="space-y-6">
                {faqs.map(({ question, answer }) => (
                  <section key={question.en}>
                    <h3 className="mb-2 text-lg font-bold text-[#d4a017]">{pick(question)}</h3>
                    <p className="text-sm leading-[1.85] text-gray-400"><LinkedText>{pick(answer)}</LinkedText></p>
                  </section>
                ))}
              </div>
            </article>

            <section
              className="rounded-2xl border border-[#d4a017]/20 p-6 md:p-8"
              style={{ background: 'linear-gradient(135deg, #200404, #2a0505)' }}
            >
              <h2 className="mb-4 text-2xl font-black text-white">
                {t(
                  'Understanding Your Responsibilities Before Playing',
                  'Fahami Tanggungjawab Anda Sebelum Bermain'
                )}
              </h2>
              <p className="mb-5 text-sm leading-[1.85] text-gray-400">
                <LinkedText>{t(
                  'Before using an account, payment feature, promotion or game, confirm that you understand the relevant rules and can make an informed, affordable decision.',
                  'Sebelum menggunakan akaun, ciri pembayaran, promosi atau permainan, pastikan anda memahami peraturan berkaitan dan boleh membuat keputusan yang bermaklumat serta dalam kemampuan.'
                )}</LinkedText>
              </p>
              <h3 className="mb-3 text-lg font-bold text-[#d4a017]">
                {t('Player responsibility checklist', 'Senarai semak tanggungjawab pemain')}
              </h3>
              <ul className="grid gap-2 text-sm leading-[1.75] text-gray-300 sm:grid-cols-2">
                {[
                  t('Keep account information accurate and secure.', 'Pastikan maklumat akaun tepat dan selamat.'),
                  t('Verify payment details before confirming.', 'Sahkan butiran pembayaran sebelum mengesahkan.'),
                  t('Read the rules for every bonus.', 'Baca peraturan bagi setiap bonus.'),
                  t('Understand a game before wagering.', 'Fahami permainan sebelum membuat pertaruhan.'),
                  t('Prevent unauthorised account access.', 'Cegah akses akaun tanpa kebenaran.'),
                  t('Risk only money you can afford to lose.', 'Pertaruhkan hanya wang yang anda mampu rugi.'),
                  t('Review responsible-gaming guidance.', 'Semak panduan permainan bertanggungjawab.'),
                ].map((item) => (
                  <li key={item} className="flex gap-2">
                    <i className="ri-checkbox-circle-line mt-0.5 text-[#d4a017]" aria-hidden="true" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-7 flex flex-wrap gap-3">
                <Link to="/faq/" className="rounded-lg border border-[#d4a017]/40 px-4 py-2.5 text-sm font-bold text-[#d4a017] hover:border-[#d4a017]">
                  {t('General FAQ', 'FAQ Umum')}
                </Link>
                <Link to="/responsible-gaming/" className="rounded-lg border border-[#d4a017]/40 px-4 py-2.5 text-sm font-bold text-[#d4a017] hover:border-[#d4a017]">
                  {t('Responsible Gaming', 'Permainan Bertanggungjawab')}
                </Link>
                <Link to="/bonus/" className="rounded-lg border border-[#d4a017]/40 px-4 py-2.5 text-sm font-bold text-[#d4a017] hover:border-[#d4a017]">
                  {t('Bonus Terms', 'Terma Bonus')}
                </Link>
                <a
                  href="https://mrcuci.org/chatroom"
                  className="rounded-lg border border-[#d4a017]/40 px-4 py-2.5 text-sm font-bold text-[#d4a017] hover:border-[#d4a017]"
                >
                  {t('Customer Support', 'Sokongan Pelanggan')}
                </a>
              </div>
              <p className="mt-6 text-sm font-black text-white">
                {t(
                  '18+ only. Gambling involves financial risk. Only play with money you can afford to lose.',
                  '18+ sahaja. Perjudian melibatkan risiko kewangan. Main hanya dengan wang yang anda mampu rugi.'
                )}
              </p>
            </section>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
